import isString from 'lodash/isString';
import type { StorageAdapter } from '@tanstack/offline-transactions';

export type IDBKey = string;

type IDBKeyRangeFactory = Pick<typeof IDBKeyRange, 'lowerBound'>;

export const isValidKey = (key: IDBValidKey): key is IDBKey => isString(key);

/**
 * Persists string-keyed offline transaction data in a dedicated IndexedDB object store.
 * Batched writes and deletes share one transaction so related changes are committed atomically.
 */
export class IndexedDBStorageAdapter implements StorageAdapter {
  readonly #indexedDbFactory: IDBFactory;
  readonly #databaseName: string;
  readonly #storeName: string;
  readonly #keyRangeFactory: IDBKeyRangeFactory | undefined;
  #database: Promise<IDBDatabase> | undefined;

  constructor(
    indexedDbFactory = indexedDB,
    databaseName: string,
    storeName = 'transactions',
    keyRangeFactory?: IDBKeyRangeFactory
  ) {
    this.#indexedDbFactory = indexedDbFactory;
    this.#databaseName = databaseName;
    this.#storeName = storeName;
    this.#keyRangeFactory = keyRangeFactory;
  }

  #open(): Promise<IDBDatabase> {
    if (this.#database) return this.#database;

    // Cache the in-flight open so concurrent operations share one connection.
    const opening = this.#openDatabase();
    this.#database = opening;
    opening
      .then((database) => {
        // An upgrade from another adapter invalidates this connection.
        const close = () => {
          database.close();
          if (this.#database === opening) this.#database = undefined;
        };
        database.onversionchange = close;
        database.onclose = close;
      })
      .catch(() => {
        if (this.#database === opening) this.#database = undefined;
      });
    return opening;
  }

  async #openDatabase(): Promise<IDBDatabase> {
    const database = await this.#openCurrentVersion();
    if (database.objectStoreNames.contains(this.#storeName)) return database;

    // IndexedDB only allows creating stores during a version upgrade.
    const nextVersion = database.version + 1;
    database.close();
    return this.#upgradeDatabase(nextVersion);
  }

  #openCurrentVersion(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      // Omitting the version discovers the database's actual current version.
      const request = this.#indexedDbFactory.open(this.#databaseName);
      request.onerror = () =>
        reject(request.error ?? new Error('Failed to open IndexedDB'));
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(this.#storeName)) {
          request.result.createObjectStore(this.#storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
    });
  }

  #upgradeDatabase(version: number): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      // Opening at a higher version triggers onupgradeneeded below.
      const request = this.#indexedDbFactory.open(this.#databaseName, version);
      request.onerror = () =>
        reject(request.error ?? new Error('Failed to upgrade IndexedDB'));
      request.onblocked = () =>
        reject(new Error('IndexedDB upgrade is blocked'));
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(this.#storeName)) {
          request.result.createObjectStore(this.#storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
    });
  }

  async #transaction<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => () => T
  ): Promise<T> {
    const database = await this.#open();
    return new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(this.#storeName, mode);
      // Preserve the request-level error because it can be more specific than
      // the error eventually exposed by the aborted transaction.
      let requestError: DOMException | null = null;
      transaction.onerror = (event) => {
        const target = event.target;
        if (
          target &&
          'error' in target &&
          target.error instanceof DOMException
        ) {
          requestError ??= target.error;
        }
      };
      // Wait for the transaction to abort before rejecting so the original error is available.
      transaction.onabort = () =>
        reject(
          requestError ??
            transaction.error ??
            new DOMException('IndexedDB transaction aborted', 'AbortError')
        );
      try {
        const result = operation(transaction.objectStore(this.#storeName));
        transaction.oncomplete = () => {
          try {
            resolve(result());
          } catch (error) {
            reject(error);
          }
        };
      } catch (error) {
        transaction.abort();
        reject(error);
      }
    });
  }

  get(key: IDBKey): Promise<string | null> {
    return this.#transaction('readonly', (store) => {
      const request = store.get(key);
      return () => {
        const value: unknown = request.result;
        if (value === undefined) return null;
        if (!isString(value))
          throw new Error('IndexedDB contains a non-string value');
        return value;
      };
    });
  }

  getPage(
    limit: number,
    cursor?: IDBKey
  ): Promise<{ values: string[]; nextCursor?: IDBKey }> {
    if (!Number.isInteger(limit) || limit <= 0) {
      return Promise.reject(
        new RangeError('Page limit must be a positive integer')
      );
    }

    return this.#transaction('readonly', (store) => {
      const entries: Array<{ key: IDBKey; value: string }> = [];

      // The cursor represents the key of the last item returned by the previous page
      // Using an exclusive lower bound (`true`) prevents that item from appearing again on the next page
      const range =
        cursor === undefined
          ? undefined
          : (this.#keyRangeFactory ?? IDBKeyRange).lowerBound(cursor, true);

      const request = store.openCursor(range);
      request.onsuccess = () => {
        const result = request.result;
        if (!result) return;

        const value: unknown = result.value;
        if (isValidKey(result.key) && isString(value)) {
          entries.push({ key: result.key, value });
        } else {
          // TODO(FO-4315): Add metrics/diagnostics for invalid IndexedDB entries.
        }

        // Fetch until one extra valid item is found to know if another page exists.
        if (entries.length <= limit) result.continue();
      };

      return () => {
        // The scan reads up to one entry past the limit to detect a next page, so drop that lookahead entry here
        const values = entries.slice(0, limit).map(({ value }) => value);

        // The last returned key is used as the next cursor.
        const lastEntry = entries.at(limit - 1);
        const nextCursor =
          entries.length > limit && lastEntry ? lastEntry.key : undefined;

        return { values, nextCursor };
      };
    });
  }

  keys(): Promise<IDBKey[]> {
    return this.#transaction('readonly', (store) => {
      const request = store.getAllKeys();
      // TODO(https://cognitedata.atlassian.net/browse/FO-4315): Add it to telemetry/instrumentation (for observability/monitoring purposes) if any key is not a string.
      return () => request.result.filter(isValidKey);
    });
  }

  count(): Promise<number> {
    return this.#transaction('readonly', (store) => {
      const request = store.count();
      return () => request.result;
    });
  }

  set(key: IDBKey, value: string): Promise<void> {
    return this.#transaction('readwrite', (store) => {
      store.put(value, key);
      return () => undefined;
    });
  }

  delete(key: IDBKey): Promise<void> {
    return this.#transaction('readwrite', (store) => {
      store.delete(key);
      return () => undefined;
    });
  }

  clear(): Promise<void> {
    return this.#transaction('readwrite', (store) => {
      store.clear();
      return () => undefined;
    });
  }

  async close(): Promise<void> {
    const database = this.#database;
    this.#database = undefined;

    if (database) {
      (await database).close();
    }
  }
}
